<template>
  <div class="roles-table">
    <div class="roles-table__wrapper">
      <table class="roles-table__table">
        <thead class="roles-table__header">
          <tr>
            <th class="roles-table__th">Role</th>
            <th class="roles-table__th">Users Assigned</th>
            <th class="roles-table__th">Date Created</th>
            <th class="roles-table__th roles-table__th--actions">Actions</th>
          </tr>
        </thead>
        <tbody class="roles-table__body">
          <tr v-for="role in roles" :key="role.id" class="roles-table__row">
            <td class="roles-table__td">
              <div class="roles-table__role-name">{{ role.name }}</div>
            </td>
            <td class="roles-table__td">
              <div class="roles-table__users">
                <span class="roles-table__users-count">{{ role.teamMembers?.length || 0 }}</span>
                <span class="roles-table__users-label">Users</span>
              </div>
            </td>
            <td class="roles-table__td">
              <span class="roles-table__date">{{ formatDate(role.createdAt) }}</span>
            </td>
            <td class="roles-table__td roles-table__td--actions">
              <Popper offsetDistance="5" placement="bottom-end">
                <button class="roles-table__actions-btn">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" fill="currentColor"/>
                  </svg>
                </button>
                <template #content="{ close }">
                  <div class="roles-table__dropdown">
                    <TeamMembersModalButton
                      :role="role"
                      title="View Team Members"
                      @member-removed="onAction"
                      :close="close"
                    />
                    <button
                      v-if="role.organisation"
                      @click="editRole(role.id, close)"
                      class="roles-table__dropdown-item"
                      :disabled="!isActionAllowed"
                    >
                      Edit Role
                    </button>
                    <button
                      v-if="role.organisation"
                      @click="deleteRole(role.id)"
                      class="roles-table__dropdown-item roles-table__dropdown-item--danger"
                      :disabled="!isActionAllowed"
                    >
                      Delete Role
                    </button>
                  </div>
                </template>
              </Popper>
            </td>
          </tr>
          <tr v-if="roles.length === 0 && !loading">
            <td colspan="4" class="roles-table__empty">
              <div class="roles-table__empty-content">
                <div class="roles-table__empty-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <p>You currently do not have any roles defined</p>
              </div>
            </td>
          </tr>
          <tr v-if="loading">
            <td colspan="4" class="roles-table__loading">
              <div class="roles-table__spinner"></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
  
<script>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import TeamMembersModalButton from "../TeamMembersModalButton.vue";
import Pagination from "@/components/navigation/Pagination";
import Popper from "vue3-popper";

export default {
  props: {
    roles: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    onAction: {
      type: Function,
      required: false,
      default: null,
    },
    totalPages: { type: Number, default: () => 0, required: true },
    currentPage: { type: Number, default: () => 0, required: true },
    isActionAllowed: Boolean,
  },

  emits: ["edit-role", "delete-role", "gotoPage"],

  components: {
    TeamMembersModalButton,
    Pagination,
    Popper,
  },

  setup(props, { emit }) {
    const searchQuery = ref("");

    function formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString();
    }

    function editRole(roleId, closeFn) {
      emit("edit-role", roleId);
      closeFn();
    }

    function deleteRole(roleId) {
      emit("delete-role", roleId);
    }

    const gotoPage = (page) => {
      this.$emit("gotoPage", page);
    };

    return {
      formatDate,
      editRole,
      deleteRole,
      gotoPage,
    };
  },
};
</script>

<style lang="scss" scoped>
.roles-table {
  width: 100%;
  
  &__wrapper {
    overflow-x: auto;
  }
  
  &__table {
    width: 100%;
    border-collapse: collapse;
  }
  
  &__header {
    background: #F8FAFC;
  }
  
  &__th {
    padding: 14px 20px;
    text-align: left;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #64748B;
    white-space: nowrap;
    
    &--actions {
      text-align: right;
    }
  }
  
  &__body {
    background: #ffffff;
  }
  
  &__row {
    border-bottom: 1px solid #E2E8F0;
    transition: background 0.2s ease;
    
    &:hover {
      background: #F8FAFC;
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  &__td {
    padding: 16px 20px;
    vertical-align: middle;
    
    &--actions {
      text-align: right;
    }
  }
  
  &__role-name {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
  }
  
  &__users {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  &__users-count {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
  }
  
  &__users-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
  }
  
  &__date {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
  }
  
  &__actions-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 8px;
    color: #64748B;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-left: auto;
    
    &:hover {
      background: #F1F5F9;
      color: #0F172A;
    }
  }
  
  &__dropdown {
    min-width: 180px;
    background: #ffffff;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 8px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  }
  
  &__dropdown-item {
    width: 100%;
    padding: 10px 12px;
    text-align: left;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s ease;
    
    &:hover:not(:disabled) {
      background: #F1F5F9;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    &--danger {
      color: #EF4444;
      
      &:hover:not(:disabled) {
        background: #FEF2F2;
      }
    }
  }
  
  &__empty {
    padding: 48px 20px;
  }
  
  &__empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    
    p {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #64748B;
      margin: 0;
    }
  }
  
  &__empty-icon {
    width: 48px;
    height: 48px;
    background: #F1F5F9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748B;
    margin-bottom: 12px;
  }
  
  &__loading {
    padding: 48px 20px;
    text-align: center;
  }
  
  &__spinner {
    width: 24px;
    height: 24px;
    border: 2px solid #E2E8F0;
    border-top-color: #4D84FF;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>